import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Maybe } from '@/shared/libs';
import { useDebouncedValue } from '@/shared/libs';
import {
  Button,
  CountryFlag,
  Dialog,
  DialogFooter,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Input,
  LoaderOverlay,
  MultiSelectFilter,
} from '@/shared/ui';
import type { Offer, TrafficType } from '@psp-aggregator/sdk';

import {
  useCatalogFacets,
  useCatalogOffers,
} from '../../libs/useCatalogOffers';

import { getOfferDisplayName } from '../../libs/offerAttributes';
import { OfferCard } from '../OfferCard';
import { OfferGeneral } from '../OfferGeneral';

export interface CatalogBrowserProps {
  actionLabel?: string;
  onAction?: () => void;
  onSelectionChange: (offers: Offer[]) => void;
  selectedIds: readonly string[];
}

export const CatalogBrowser: FC<CatalogBrowserProps> = ({
  actionLabel,
  onAction,
  onSelectionChange,
  selectedIds,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [trafficTypes, setTrafficTypes] = useState<TrafficType[]>([]);
  const [integrationMethods, setIntegrationMethods] = useState<string[]>([]);
  const [openedOffer, setOpenedOffer] = useState<Maybe<Offer>>(null);
  const debouncedSearch = useDebouncedValue(search.trim());
  const facets = useCatalogFacets();
  const offers = useCatalogOffers({
    countries: countries.length > 0 ? countries : undefined,
    currencies: currencies.length > 0 ? currencies : undefined,
    integrationMethods:
      integrationMethods.length > 0 ? integrationMethods : undefined,
    search: debouncedSearch || undefined,
    trafficTypes: trafficTypes.length > 0 ? trafficTypes : undefined,
  });
  const visibleOffers = offers.data?.items ?? [];

  const toggleOffer = (offer: Offer): void => {
    const current =
      offers.data?.items.filter((item) => selectedIds.includes(item.id)) ?? [];

    onSelectionChange(
      selectedIds.includes(offer.id)
        ? current.filter((item) => item.id !== offer.id)
        : [...current, offer],
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4">
        <Input
          aria-label={t('listToolbar.search')}
          placeholder={t('pages.catalog.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label={t('pages.catalog.filters.country')}
            options={(facets.data?.countries ?? []).map((code) => ({
              icon: <CountryFlag code={code} />,
              label: code,
              value: code,
            }))}
            selected={countries}
            onChange={setCountries}
          />
          <MultiSelectFilter
            label={t('pages.catalog.filters.currency')}
            options={(facets.data?.currencies ?? []).map((code) => ({
              label: code,
              value: code,
            }))}
            selected={currencies}
            onChange={setCurrencies}
          />
          <MultiSelectFilter
            label={t('pages.admin.offers.fields.trafficType')}
            options={(facets.data?.traffic_types ?? []).map((type) => ({
              label: type,
              value: type,
            }))}
            selected={trafficTypes}
            onChange={(next) => setTrafficTypes(next as TrafficType[])}
          />
          <MultiSelectFilter
            label={t('pages.catalog.filters.integration')}
            options={(facets.data?.integration_methods ?? []).map((code) => ({
              label: code,
              value: code,
            }))}
            selected={integrationMethods}
            onChange={setIntegrationMethods}
          />
        </div>
        {!!actionLabel && !!onAction && (
          <Button
            className="w-full sm:w-auto"
            disabled={selectedIds.length === 0}
            type="button"
            variant="brand"
            onClick={onAction}>
            {actionLabel} ({selectedIds.length})
          </Button>
        )}
      </div>
      {offers.isLoading && <LoaderOverlay show />}
      {offers.isError && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('errors.catalog.loadFailed')}</EmptyTitle>
            <EmptyDescription>{t('errors.unknown')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      {!offers.isLoading && !offers.isError && visibleOffers.length === 0 && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('listEmpty.filteredTitle')}</EmptyTitle>
            <EmptyDescription>
              {t('listEmpty.filteredDescription')}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visibleOffers.map((offer) => {
          const selected = selectedIds.includes(offer.id);
          const paused = (offer.status ?? 'active') === 'paused';

          return (
            <OfferCard
              key={offer.id}
              mode="catalog"
              offer={offer}
              paused={paused}
              selected={selected}
              onOpen={setOpenedOffer}
              onSelect={paused ? undefined : toggleOffer}
            />
          );
        })}
      </div>
      {!!openedOffer && (
        <Dialog
          title={getOfferDisplayName(openedOffer)}
          width={640}
          onClose={() => setOpenedOffer(null)}>
          <div className="space-y-4">
            <OfferGeneral offer={openedOffer} />
          </div>
          <DialogFooter
            endSlot={
              <Button type="button" onClick={() => setOpenedOffer(null)}>
                {t('buttons.close')}
              </Button>
            }
          />
        </Dialog>
      )}
    </div>
  );
};

CatalogBrowser.displayName = 'CatalogBrowser';
